/**
 * UserTransactions Component
 * 
 * Displays recent transactions for a user in a table format.
 * Table: Type Badge, Points (colored), Date, Source Badge
 * "Add Transaction" button (opens CreateTransactionModal for EARN or ADJUSTMENT)
 * "View All Transactions" link to /transactions?user_id=X
 * Shows max 10 recent
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, type Column } from '@/components/common/Table';
import { TransactionTypeBadge, SourceSystemBadge } from '@/components/common/Badge';
import { CreateTransactionModal } from '@/components/transactions/CreateTransactionModal';
import type { TransactionResponse } from '@/app/types/transactions';
import './UserTransactions.scss';

interface UserTransactionsProps {
  transactions: TransactionResponse[];
  userId: number;
  /** User's partner_product_id for transaction creation */
  partnerProductId?: number | null;
  onCreateAdjustment: () => void;
}

export const UserTransactions: React.FC<UserTransactionsProps> = ({
  transactions,
  userId,
  partnerProductId,
  onCreateAdjustment,
}) => {
  const navigate = useNavigate();
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);

  const handleTransactionSuccess = useCallback((_transactionId: string) => {
    onCreateAdjustment();
    setIsTransactionModalOpen(false);
  }, [onCreateAdjustment]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const columns: Column<TransactionResponse>[] = [
    {
      key: 'transaction_type',
      header: 'Type',
      width: '15%',
      render: (tx) => <TransactionTypeBadge type={tx.transaction_type} />,
    },
    {
      key: 'point_value',
      header: 'Points',
      width: '15%',
      align: 'right',
      render: (tx) => (
        <span className={`user-transactions-points ${tx.point_value >= 0 ? 'positive' : 'negative'}`}>
          {tx.point_value >= 0 ? '+' : ''}{tx.point_value.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      width: '30%',
      render: (tx) => formatDate(tx.created_at),
    },
    {
      key: 'source_system',
      header: 'Source',
      width: '15%',
      render: (tx) => <SourceSystemBadge source={tx.source_system} />,
    },
    {
      key: 'actions',
      header: '',
      width: '25%',
      render: (tx) => (
        <button
          className="btn-view"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/transactions/${tx.transaction_id}`);
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="user-transactions">
      <div className="user-transactions-header">
        <h3>Recent Transactions</h3>
        <div className="user-transactions-actions">
          <button
            className="user-transactions-create-btn"
            onClick={() => setIsTransactionModalOpen(true)}
          >
            Add Transaction
          </button>
          <a
            href={`#/transactions?user_id=${userId}`}
            className="user-transactions-view-all-link"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/transactions?user_id=${userId}`);
            }}
          >
            View All Transactions
          </a>
        </div>
      </div>

      {transactions.length === 0 ? (
        <div className="user-transactions-empty">
          <p>No transactions found for this user.</p>
        </div>
      ) : (
        <>
          <Table<TransactionResponse>
            columns={columns}
            data={transactions}
            keyExtractor={(tx) => tx.transaction_id}
            emptyMessage="No transactions found"
          />
          {transactions.length >= 10 && (
            <div className="user-transactions-footer">
              <a
                href={`#/transactions?user_id=${userId}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/transactions?user_id=${userId}`);
                }}
              >
                View all {transactions.length}+ transactions →
              </a>
            </div>
          )}
        </>
      )}

      <CreateTransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => setIsTransactionModalOpen(false)}
        onSuccess={handleTransactionSuccess}
        prefillUserId={userId}
        prefillPartnerProductId={partnerProductId}
      />
    </div>
  );
};

export default UserTransactions;
